import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Shield,
  Users,
  Building2,
  CircleDollarSign,
  Clock,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle,
  Trash2,
  RotateCcw,
  Power,
  ArrowLeft,
  Activity,
} from "lucide-react";

const SUPER_ADMIN_EMAIL = "jvroomen1407@gmail.com";

function StatCard({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    orange: "bg-orange-600 text-white",
    green: "bg-emerald-600 text-white",
    red: "bg-red-600 text-white",
    amber: "bg-amber-500 text-slate-900",
  };
  return (
    <div className={`pb-card p-5 ${tones[tone]}`} data-testid={`admin-stat-${label}`}>
      <div className="flex items-center gap-3">
        <Icon size={22} />
        <div className="text-xs font-black uppercase tracking-widest opacity-90">{label}</div>
      </div>
      <div className="mt-2 pb-heading text-4xl font-black tabular-nums">{value}</div>
      {sub && <div className="text-xs font-bold opacity-85 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Betalend", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    trialing: { label: "Proef", cls: "bg-orange-100 text-orange-800 border-orange-300" },
    expired: { label: "Verlopen", cls: "bg-red-100 text-red-800 border-red-300" },
    past_due: { label: "Achterstallig", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    canceled: { label: "Opgezegd", cls: "bg-slate-200 text-slate-700 border-slate-300" },
    unknown: { label: "Onbekend", cls: "bg-slate-100 text-slate-500 border-slate-300" },
  };
  const s = map[status] || map.unknown;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("companies");
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/companies"),
        api.get("/admin/audit?limit=100"),
      ]);
      setStats(s.data);
      setCompanies(c.data.companies || []);
      setAudit(a.data.entries || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kon dashboard niet laden");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return companies.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!t) return true;
      return (
        (c.name || "").toLowerCase().includes(t)
        || (c.owner_email || "").toLowerCase().includes(t)
        || (c.owner_name || "").toLowerCase().includes(t)
        || (c.kvk || "").toLowerCase().includes(t)
      );
    });
  }, [companies, q, statusFilter]);

  if (user === null) {
    return <div className="flex items-center justify-center h-screen text-slate-500 font-semibold">Laden…</div>;
  }
  if (!user || (user.email || "").toLowerCase() !== SUPER_ADMIN_EMAIL) {
    return <Navigate to="/planbord" replace />;
  }

  const extendTrial = async (c, days) => {
    if (!days) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/companies/${c.id}/extend-trial`, { days });
      toast.success(`Proef +${days} dagen`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verlengen mislukt");
    }
    setBusyId(null);
  };

  const promptExtend = async (c) => {
    const raw = window.prompt(`Aantal dagen om proef van ${c.name} te verlengen?`, "14");
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) { toast.error("Ongeldig aantal"); return; }
    await extendTrial(c, n);
  };

  const markPaid = async (c) => {
    // Vraag admin welk pakket (bepaalt hoeveel kantoormedewerkers deze klant mag aanmaken).
    const current = c.tier || "max";
    const raw = window.prompt(
      `${c.name} handmatig op 'Paid' zetten.\n\nWelk pakket?\n  start = 2 kantoormedewerkers\n  pro   = 4 kantoormedewerkers\n  max   = 6 kantoormedewerkers\n\n(Stripe wordt NIET aangeroepen)`,
      current,
    );
    if (!raw) return;
    const tier = raw.trim().toLowerCase();
    if (!["start", "pro", "max"].includes(tier)) {
      toast.error("Ongeldig pakket. Kies start, pro of max.");
      return;
    }
    setBusyId(c.id);
    try {
      await api.post(`/admin/companies/${c.id}/mark-paid`, { note: "manual VIP by admin", tier });
      toast.success(`${c.name} → Betalend (${tier.toUpperCase()})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mislukt");
    }
    setBusyId(null);
  };

  const setTier = async (c, tier) => {
    if (!tier || tier === c.tier) return;
    setBusyId(c.id);
    try {
      const res = await api.patch(`/admin/companies/${c.id}/tier`, { tier });
      toast.success(`${c.name} → pakket ${tier.toUpperCase()} · ${res.data?.office_seats || "?"} kantoormedewerkers`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pakket wijzigen mislukt");
    }
    setBusyId(null);
  };

  const setStatus = async (c, status) => {
    setBusyId(c.id);
    try {
      await api.patch(`/admin/companies/${c.id}/status`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status niet opgeslagen");
    }
    setBusyId(null);
  };

  const deactivate = async (c) => {
    if (!window.confirm(`Bedrijf ${c.name} DEACTIVEREN? Alle gebruikers worden uitgelogd.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/companies/${c.id}/deactivate`);
      toast.success(`${c.name} gedeactiveerd`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivatie mislukt");
    }
    setBusyId(null);
  };

  const reactivate = async (c) => {
    setBusyId(c.id);
    try {
      await api.post(`/admin/companies/${c.id}/reactivate`);
      toast.success(`${c.name} heractiveerd (+7 dagen proef)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heractivatie mislukt");
    }
    setBusyId(null);
  };

  const del = async (c) => {
    const confirm1 = window.prompt(`⚠️ HARD DELETE: type "${c.name}" om te bevestigen.`);
    if (confirm1 !== c.name) { toast.info("Verwijderen geannuleerd."); return; }
    if (!window.confirm(`Definitief ALLES van ${c.name} verwijderen? Dit kan NIET ongedaan worden.`)) return;
    setBusyId(c.id);
    try {
      const r = await api.delete(`/admin/companies/${c.id}`);
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${c.name} verwijderd (${total} records)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
    setBusyId(null);
  };

  const bucket = stats?.by_status || {};

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="admin-dashboard-page">
      <header className="border-b-4 border-orange-600 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Shield size={20} className="text-orange-400" />
            <div>
              <div className="pb-heading text-xl font-black">Platform Admin</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Ploegbord · super-admin dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/planbord" className="text-white text-xs font-black uppercase tracking-widest hover:text-orange-400 flex items-center gap-1" data-testid="admin-back-app">
              <ArrowLeft size={14} /> Terug naar app
            </Link>
            <button onClick={load} className="pb-btn-outline text-white border-white/40 !py-1 !px-3 flex items-center gap-1" data-testid="admin-refresh">
              <RefreshCw size={14} /> Ververs
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Building2} label="Bedrijven" value={stats?.total_companies ?? "…"} sub={`${stats?.total_users ?? 0} actieve gebruikers`} tone="slate" />
          <StatCard icon={CircleDollarSign} label="Betalend" value={bucket.active ?? 0} sub={`${stats?.total_seats_paid ?? 0} stoelen`} tone="green" />
          <StatCard icon={Clock} label="Proef" value={bucket.trialing ?? 0} sub={`${bucket.expired ?? 0} verlopen`} tone="orange" />
          <StatCard icon={AlertTriangle} label="Verlopen / Achterstallig" value={(bucket.expired ?? 0) + (bucket.past_due ?? 0)} sub={`${bucket.canceled ?? 0} opgezegd`} tone="red" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b-2 border-slate-200">
          <button onClick={() => setTab("companies")} className={`px-4 py-2 -mb-0.5 border-b-4 text-xs font-black uppercase tracking-widest flex items-center gap-2 ${tab === "companies" ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid="admin-tab-companies">
            <Building2 size={14}/> Klanten <span className="opacity-60">({companies.length})</span>
          </button>
          <button onClick={() => setTab("audit")} className={`px-4 py-2 -mb-0.5 border-b-4 text-xs font-black uppercase tracking-widest flex items-center gap-2 ${tab === "audit" ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid="admin-tab-audit">
            <Activity size={14}/> Activiteit <span className="opacity-60">({audit.length})</span>
          </button>
        </div>

        {tab === "audit" ? (
          <AuditPanel audit={audit} companies={companies} loading={loading} />
        ) : (
        <>
        {/* Filters */}
        <div className="pb-card p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search size={16} className="text-slate-500 shrink-0" />
            <input
              className="pb-input flex-1"
              placeholder="Zoek op bedrijf, owner, e-mail, KVK…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            {["all", "trialing", "active", "expired", "past_due", "canceled"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-full border-2 text-[10px] font-black uppercase tracking-widest ${statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                data-testid={`admin-filter-${s}`}
              >
                {s === "all" ? "Alle" : s === "trialing" ? "Proef" : s === "active" ? "Betalend" : s === "expired" ? "Verlopen" : s === "past_due" ? "Achterstallig" : "Opgezegd"}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="pb-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs font-black uppercase tracking-widest text-slate-700">
              <tr>
                <th className="p-3">Bedrijf</th>
                <th className="p-3">Owner</th>
                <th className="p-3">Aangemaakt</th>
                <th className="p-3">Laatste login</th>
                <th className="p-3">Status</th>
                <th className="p-3">Pakket</th>
                <th className="p-3 text-right">Projecten</th>
                <th className="p-3 text-right">Users</th>
                <th className="p-3">Trial eindigt</th>
                <th className="p-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="p-8 text-center text-slate-500">Laden…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-slate-500">Geen bedrijven gevonden.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className={`border-t border-slate-200 ${busyId === c.id ? "opacity-50" : ""}`} data-testid={`admin-row-${c.id}`}>
                  <td className="p-3">
                    <div className="font-black text-slate-900">{c.name || "(geen naam)"}</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {c.demo_mode ? "DEMO · " : ""}{c.kvk ? `KVK ${c.kvk}` : ""}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-bold">{c.owner_name || "-"}</div>
                    <div className="text-xs text-slate-500 break-all">{c.owner_email || "-"}</div>
                  </td>
                  <td className="p-3 text-xs text-slate-700 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="p-3 text-xs text-slate-700 whitespace-nowrap">{fmtDateTime(c.owner_last_login_at)}</td>
                  <td className="p-3"><StatusBadge status={c.status} /></td>
                  <td className="p-3 text-xs">
                    <select
                      value={c.tier || ""}
                      onChange={(e) => setTier(c, e.target.value)}
                      disabled={busyId === c.id}
                      className="pb-input !min-h-[32px] !py-1 !px-2 text-[11px] font-black uppercase"
                      data-testid={`admin-tier-select-${c.id}`}
                      title="Pakket wijzigen — bepaalt aantal kantoormedewerkers (2/4/6)"
                    >
                      <option value="" disabled>— kies —</option>
                      <option value="start">Start · 2 kantoor</option>
                      <option value="pro">Pro · 4 kantoor</option>
                      <option value="max">Max · 6 kantoor</option>
                    </select>
                    {c.seats_paid > 0 && (
                      <div className="text-[10px] text-slate-500 mt-1">+{c.seats_paid} extra uitvoerder(s)</div>
                    )}
                  </td>
                  <td className="p-3 text-right font-black tabular-nums">{c.project_count}</td>
                  <td className="p-3 text-right font-black tabular-nums">{c.user_count}</td>
                  <td className="p-3 text-xs text-slate-700 whitespace-nowrap">{fmtDate(c.trial_ends_at)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      <button onClick={() => extendTrial(c, 7)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-orange-600 text-orange-700 text-[10px] font-black uppercase tracking-widest hover:bg-orange-50" data-testid={`admin-ext-7-${c.id}`}>+7d</button>
                      <button onClick={() => extendTrial(c, 14)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-orange-600 text-orange-700 text-[10px] font-black uppercase tracking-widest hover:bg-orange-50" data-testid={`admin-ext-14-${c.id}`}>+14d</button>
                      <button onClick={() => extendTrial(c, 30)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-orange-600 text-orange-700 text-[10px] font-black uppercase tracking-widest hover:bg-orange-50" data-testid={`admin-ext-30-${c.id}`}>+30d</button>
                      <button onClick={() => promptExtend(c)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-slate-400 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50" data-testid={`admin-ext-custom-${c.id}`}>Custom…</button>
                      {c.status !== "active" && (
                        <button onClick={() => markPaid(c)} disabled={busyId === c.id} className="px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 flex items-center gap-1" data-testid={`admin-mark-paid-${c.id}`}>
                          <CheckCircle2 size={12}/> Paid
                        </button>
                      )}
                      {c.status === "active" && (
                        <button onClick={() => setStatus(c, "expired")} disabled={busyId === c.id} className="px-2 py-1 rounded border border-red-500 text-red-600 text-[10px] font-black uppercase tracking-widest hover:bg-red-50" data-testid={`admin-expire-${c.id}`}>
                          <XCircle size={12}/> Verlopen
                        </button>
                      )}
                      {c.status !== "canceled" && (
                        <button onClick={() => deactivate(c)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-slate-500 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 flex items-center gap-1" data-testid={`admin-deact-${c.id}`}>
                          <Power size={12}/> Off
                        </button>
                      )}
                      {c.status === "canceled" && (
                        <button onClick={() => reactivate(c)} disabled={busyId === c.id} className="px-2 py-1 rounded border border-emerald-500 text-emerald-700 text-[10px] font-black uppercase tracking-widest hover:bg-emerald-50 flex items-center gap-1" data-testid={`admin-react-${c.id}`}>
                          <RotateCcw size={12}/> On
                        </button>
                      )}
                      <button onClick={() => del(c)} disabled={busyId === c.id} className="px-2 py-1 rounded bg-red-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-red-700 flex items-center gap-1" data-testid={`admin-delete-${c.id}`}>
                        <Trash2 size={12}/> Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-center text-xs text-slate-500 font-bold">
          Alleen zichtbaar voor <span className="font-black text-slate-700">{SUPER_ADMIN_EMAIL}</span>. Alle acties worden gelogd.
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function AuditPanel({ audit, companies, loading }) {
  const nameFor = (cid) => {
    const c = companies.find((x) => x.id === cid);
    return c ? c.name : cid?.slice(-8) || "-";
  };
  const actionColor = {
    extend_trial: "bg-orange-100 text-orange-800 border-orange-300",
    mark_paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
    set_status: "bg-slate-100 text-slate-800 border-slate-300",
    deactivate: "bg-red-100 text-red-800 border-red-300",
    reactivate: "bg-emerald-100 text-emerald-800 border-emerald-300",
    delete: "bg-red-200 text-red-900 border-red-400",
  };
  return (
    <div className="pb-card overflow-x-auto" data-testid="admin-audit-panel">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-left text-xs font-black uppercase tracking-widest text-slate-700">
          <tr>
            <th className="p-3">Wanneer</th>
            <th className="p-3">Admin</th>
            <th className="p-3">Actie</th>
            <th className="p-3">Bedrijf</th>
            <th className="p-3">Details</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={5} className="p-8 text-center text-slate-500">Laden…</td></tr>
          )}
          {!loading && audit.length === 0 && (
            <tr><td colSpan={5} className="p-8 text-center text-slate-500">Nog geen activiteit vastgelegd.</td></tr>
          )}
          {audit.map((e) => (
            <tr key={e.id} className="border-t border-slate-200" data-testid={`admin-audit-row-${e.id}`}>
              <td className="p-3 text-xs text-slate-700 whitespace-nowrap tabular-nums">{new Date(e.at).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
              <td className="p-3 text-xs">{e.admin_email}</td>
              <td className="p-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${actionColor[e.action] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                  {e.action}
                </span>
              </td>
              <td className="p-3 font-bold">{nameFor(e.company_id)}</td>
              <td className="p-3 text-xs text-slate-600 font-mono max-w-md truncate">{e.meta && Object.keys(e.meta).length ? JSON.stringify(e.meta) : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-3 text-xs text-slate-500 text-center border-t border-slate-200">
        Laatste {audit.length} acties · alle wijzigingen op klant-status en trials worden hier gelogd.
      </div>
    </div>
  );
}
